<script>
    import app from "../stores/app";
    import R, {LOADER} from "../lib/requests";
    import {IMAGE} from "../lib/constants";
    import Alert from "../stores/alerts";
    import {createEventDispatcher} from 'svelte/internal';
    import AppIcon from "../components/icons/AppIcon.svelte";

    export let image;
    export let index;
    export let tourId = 0;

    let imageObject = null;
    let degrees = 0;
    let canvas = null;
    let context = null;
    let loading = false;
    let uploadError = false;

    const DEBUG = false;

    const dispatch = createEventDispatcher();

    const onClick = () => {
        if (image.id > 0) {
            dispatch('click', index);
        }
    }

    const onDelete = () => {
        if (!uploadError) {
            Alert.confirm("Delete image", "Delete image from tour?", () => dispatch('delete', image.index));
            return;
        }
        dispatch('delete', image.index);
    }

    const uploadImage = () => {

        const onSuccess = result => {
            degrees = 0;
            image.id = result.id;
            image.filename = result.filename;
            delete (image.tour_id);
            delete (image.upload);
            dispatch('upload_complete', image);
        }

        const onFailure = err => {
            uploadError = true;
            let message = 'An error occurred while uploading the image!';
            if (err.code) {
                if (err.code === 'not_found') {
                    message = "Tour not found!";
                } else {
                    message = err.message;
                }
                if (err.params) {
                    message += `\nFile name: ${err.params}`;
                }
            }
            Alert.warning("Upload image", message);
            dispatch('upload_error', image);
        };

        canvas.toBlob(async (blob) => {
            if (DEBUG) {
                setTimeout(function () {
                    image.id = Math.random();
                    onSuccess(image);
                    // test error
                    // onFailure({error:"fail"});
                }, 2000);
                return;
            }

            let data = {
                tour_id: tourId,
                filename: image.filename,
                title: image.title,
                view_index: image.view_index,
            }

            await R.handle(R.tour.uploadImage, {data, blob}, onSuccess, onFailure);

        }, image.type);
    }

    const rotateLeft = () => {
        degrees -= 90;
        setImageData();
    }

    const rotateRight = () => {
        degrees += 90;
        setImageData();
    }

    const initCanvas = () => {
        if (canvas === null) {
            canvas = document.getElementById('cnv-' + image.index);
            context = canvas.getContext('2d');
        } else {
            context.save();
            context.setTransform(1, 0, 0, 1, 0, 0);
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.restore();
        }
    }

    const setCanvasSize = (width, height, degrees) => {
        let cnv_width = width;
        let cnv_height = height;
        if (degrees % 180 == 90) {
            cnv_width = height;
            cnv_height = width;
        }

        if (cnv_width >= cnv_height && cnv_width > IMAGE.MAX_WIDTH) {
            cnv_height *= IMAGE.MAX_WIDTH / cnv_width;
            cnv_width = IMAGE.MAX_WIDTH;
        } else if (cnv_height > IMAGE.MAX_HEIGHT) {
            cnv_width *= IMAGE.MAX_HEIGHT / cnv_height;
            cnv_height = IMAGE.MAX_HEIGHT;
        }

        canvas.width = cnv_width;
        canvas.height = cnv_height;
    }

    const setImageData = () => {
        initCanvas();

        let imageObj = new Image();
        imageObj.onload = function () {

            if (!image.width) {
                image.width = imageObj.width;
                image.height = imageObj.height;
            }

            setCanvasSize(imageObj.width, imageObj.height, degrees);

            context.clearRect(0, 0, canvas.width, canvas.height);
            context.save();
            context.translate(canvas.width / 2, canvas.height / 2);
            context.rotate(degrees * Math.PI / 180);
            if (degrees % 180 == 90) {
                context.drawImage(imageObj, -canvas.height / 2, -canvas.width / 2, canvas.height, canvas.width);
            } else {
                context.drawImage(imageObj, -canvas.width / 2, -canvas.height / 2, canvas.width, canvas.height);
            }
            context.restore();
        }
        imageObj.src = imageObject;
    }

    if (image.fileObj != null) {
        let reader = new FileReader();
        reader.onload = event => {
            imageObject = event.target.result;
            delete (image.fileObj);
            image.width = 0;
            image.height = 0;

            setImageData();
        };
        reader.readAsDataURL(image.fileObj);
    }

    $: {
        if (image.upload) {
            image.upload = false;
            uploadImage();
        }
    }
</script>

<canvas style="display:none; border: 1px solid #000;"
        id="cnv-{ image.index }"></canvas>

<div class="tour-image">
    <div class="heading text-gray">
        Image { image.view_index }
        <div>
            {#if image.id == 0}
                <button class="btn btn-default btn-xs" on:click="{ rotateLeft }">
                    <AppIcon icon="rotate-left-solid" size="14"/>
                </button>
                <button class="btn btn-default btn-xs" on:click="{ rotateRight }">
                    <AppIcon icon="rotate-right-solid" size="14"/>
                </button>
            {/if}
            <button class="btn btn-danger btn-xs" title="delete image" on:click="{ onDelete }">
                <AppIcon icon="trash"  size="14" strokeWidth="2"/>
            </button>
        </div>
    </div>
    <div class="image" class:active="{ image.id > 0 }" on:click="{ onClick }">
        <div class="image-inner" style="transform: rotate({ degrees }deg);" title="{ image.filename }">
            {#if image.id == 0}
                <img src="{ imageObject }" alt="tour image"/>
            {:else}
                <img src="{ $app.tour_image_path }/{ image.filename }"
                     alt="tour image"/>
            {/if}
        </div>

        {#if uploadError}
            <div class="upload-error" title="image was not uploaded!">
                <AppIcon icon="exclamation-triangle-solid" size="30"/>
            </div>
        {/if}
    </div>

    <div class="content">
        <input type="text" class="form-control form-control-sm" bind:value="{ image.title }" placeholder="title">
    </div>
</div>

<style>
    .tour-image {
        position: relative;
        background: #fff;
        border: var(--default-border);
        border-radius: var(--default-border-radius);
        padding: 10px;
    }

    .tour-image .heading {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 5px;
    }

    .tour-image .image {
        position: relative;
        width: 100%;
        background: var(--image-box-background);
        border-right: 4px;
        border-radius: var(--default-border-radius);
        overflow: hidden;
        transition: all .2s ease;
    }

    .tour-image .image img {
        display: block;
        width: 100%;
    }

    .tour-image .image .upload-error {
        display: flex;
        position: absolute;
        top: 0;
        bottom: 0;
        width: 100%;
        color: #fff;
        background: rgba(255, 0, 0, 0.4);
    }

    .tour-image .content {
        position: relative;
        height: 100%;
        margin-top: 5px;
    }

    .image-inner {
        transition: .3s;
        display: flex;
        align-items: center;
        height: 100%;
    }
</style>
